import { useEffect, useRef, useState } from 'react';

import { token } from '@bp-files/ui';
import type { SignatureView } from '@/lib/api';
import { inkToStrokes, type SignatureInk } from './signatureInk';
import { SIGNATURE_COPY } from './signature.copy';

/**
 * The signature already on the account, drawn from its own strokes (USR-13).
 *
 * **Rendered rather than shown**, and that is the whole payoff of storing a
 * path instead of a picture: there is no image to be too small or too large,
 * because the mark is drawn at whatever size this card gives it — and it will
 * be drawn again, sharply, at whatever size a document places it.
 *
 * **It parses `ink` here and nowhere else.** `lib/api.ts` keeps the field a
 * string on purpose, because it is the canonical text the server's digest was
 * taken over; parsing it at the boundary and re-serializing anywhere would
 * produce something whose hash no longer matches the `inkSha256` beside it.
 *
 * **A mark that will not parse renders as nothing, not as a crash.** The field
 * is server-supplied and schema-checked as a string, but its *contents* are
 * only meaningful to this module — and a stored row from a format this build
 * does not know should leave the person their remove button rather than an
 * error boundary.
 */

const PREVIEW_HEIGHT = 56;

function parseInk(ink: string): SignatureInk | undefined {
  try {
    const parsed: unknown = JSON.parse(ink);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !Array.isArray((parsed as SignatureInk).strokes) ||
      typeof (parsed as SignatureInk).aspect !== 'number'
    ) {
      return undefined;
    }

    return parsed as SignatureInk;
  } catch {
    return undefined;
  }
}

export interface StoredSignatureProps {
  signature: SignatureView;
  busy: boolean;
  onRemove: () => void;
}

export function StoredSignature({ signature, busy, onRemove }: StoredSignatureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [confirming, setConfirming] = useState(false);

  const ink = parseInk(signature.ink);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    // jsdom gives a canvas no context; the card's text is what a component
    // test asserts on.
    if (!canvas || !context || ink === undefined) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = PREVIEW_HEIGHT * ink.aspect;

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(PREVIEW_HEIGHT * ratio);
    canvas.style.width = `${String(Math.floor(width))}px`;
    canvas.style.height = `${String(PREVIEW_HEIGHT)}px`;

    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, width, PREVIEW_HEIGHT);
    context.strokeStyle = token('bp-ink');
    context.lineCap = 'round';
    context.lineJoin = 'round';

    for (const stroke of inkToStrokes(ink, PREVIEW_HEIGHT)) {
      for (let i = 1; i < stroke.length; i += 1) {
        const from = stroke[i - 1];
        const to = stroke[i];
        if (from === undefined || to === undefined) continue;

        context.beginPath();
        context.lineWidth = (from.w + to.w) / 2;
        context.moveTo(from.x, from.y);
        context.lineTo(to.x, to.y);
        context.stroke();
      }
    }
  }, [ink]);

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border p-4"
      style={{ borderColor: token('bp-border'), background: token('bp-card') }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="rounded-md p-2"
          style={{ background: token('bp-paper') }}
          data-testid="stored-signature"
        >
          <canvas ref={canvasRef} role="img" aria-label={SIGNATURE_COPY.stored.alt} />
        </div>

        {confirming ? (
          <div className="flex items-center gap-2">
            <span className="text-sm">{SIGNATURE_COPY.stored.confirm}</span>
            <button
              type="button"
              disabled={busy}
              onClick={onRemove}
              className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
              style={{ borderColor: token('bp-danger'), color: token('bp-danger') }}
            >
              {busy ? SIGNATURE_COPY.stored.removing : SIGNATURE_COPY.stored.confirmYes}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
              }}
              className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-elevated"
              style={{ borderColor: token('bp-border-strong') }}
            >
              {SIGNATURE_COPY.stored.confirmNo}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setConfirming(true);
            }}
            className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-elevated"
            style={{ borderColor: token('bp-border-strong') }}
          >
            {SIGNATURE_COPY.stored.remove}
          </button>
        )}
      </div>

      <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
        {SIGNATURE_COPY.stored.since(new Date(signature.createdAt).toLocaleDateString())}
      </p>
    </div>
  );
}
